import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import Editor from '@monaco-editor/react';

const EditorContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
`;

const EditorModal = styled.div`
  background: white;
  border-radius: 8px;
  width: 90vw;
  height: 90vh;
  max-width: 1200px;
  max-height: 800px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const EditorHeader = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid #e1e5e9;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f8f9fa;
  border-radius: 8px 8px 0 0;
`;

const EditorTitle = styled.h3`
  margin: 0;
  color: #1f2937;
  font-size: 18px;
  font-weight: 600;
`;

const EditorInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const BlockName = styled.span`
  color: #6b7280;
  font-size: 14px;
`;

const EditorMeta = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const MetaItem = styled.span`
  background: #f3f4f6;
  color: #374151;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: #6b7280;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s;

  &:hover {
    background: #f3f4f6;
    color: #374151;
  }
`;

const EditorBody = styled.div`
  flex: 1;
  display: flex;
  overflow: hidden;
`;

const EditorPane = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const PaneHeader = styled.div`
  padding: 12px 16px;
  border-bottom: 1px solid #e1e5e9;
  background: #f9fafb;
  font-weight: 600;
  color: #374151;
  font-size: 14px;
`;

const EditorWrapper = styled.div`
  flex: 1;
  position: relative;
`;

const PreviewPane = styled.div`
  flex: 1;
  border-left: 1px solid #e1e5e9;
  display: flex;
  flex-direction: column;
  background: #f9fafb;
`;

const PreviewContent = styled.div`
  flex: 1;
  padding: 16px;
  overflow-y: auto;
  background: white;
  margin: 16px;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
`;

const EditorActions = styled.div`
  padding: 16px 24px;
  border-top: 1px solid #e1e5e9;
  background: #f8f9fa;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  align-items: center;
  border-radius: 0 0 8px 8px;
`;

const ActionGroup = styled.div`
  display: flex;
  gap: 12px;
`;

const ActionButton = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: 10px 20px;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  font-size: 14px;

  ${props => {
    switch (props.variant) {
      case 'primary':
        return `
          background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
          color: white;
          &:hover {
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
          }
        `;
      case 'danger':
        return `
          background: #ef4444;
          color: white;
          &:hover {
            background: #dc2626;
          }
        `;
      default:
        return `
          background: white;
          color: #374151;
          border: 1px solid #d1d5db;
          &:hover {
            background: #f9fafb;
          }
        `;
    }
  }}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const HelpText = styled.div`
  color: #6b7280;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StatusIndicator = styled.div<{ status: 'valid' | 'invalid' }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => props.status === 'valid' ? '#10b981' : '#ef4444'};
`;

const ErrorMessage = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 12px;
  margin: 8px 16px;
`;

interface HtmlEditorProps {
  isOpen: boolean;
  blockName: string;
  blockCategory: string;
  initialHtml: string;
  originalHtml: string;
  onSave: (html: string) => void;
  onClose: () => void;
}

export const HtmlEditor: React.FC<HtmlEditorProps> = ({
  isOpen,
  blockName,
  blockCategory,
  initialHtml,
  originalHtml,
  onSave,
  onClose,
}) => {
  const [html, setHtml] = useState(initialHtml);
  const [isValid, setIsValid] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setHtml(initialHtml);
  }, [initialHtml]);

  const validateHtml = (htmlContent: string): boolean => {
    try {
      // Basic HTML validation - check for balanced tags
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlContent, 'text/html');
      const parserError = doc.querySelector('parsererror');
      
      if (parserError) {
        setError('Invalid HTML structure. Please check your syntax.');
        setIsValid(false);
        return false;
      }

      setError(null);
      setIsValid(true);
      return true;
    } catch (err) {
      setError('HTML parsing error. Please check your syntax.');
      setIsValid(false);
      return false;
    }
  };

  const handleEditorChange = (value: string | undefined) => {
    const newHtml = value || '';
    setHtml(newHtml);
    validateHtml(newHtml);
  };

  const handleSave = () => {
    if (validateHtml(html)) {
      onSave(html);
      onClose();
    }
  };

  const handleCancel = () => {
    setHtml(initialHtml);
    setError(null);
    setIsValid(true);
    onClose();
  };

  const handleReset = () => {
    if (window.confirm('Reset to original HTML? This will discard all your changes.')) {
      setHtml(originalHtml);
      validateHtml(originalHtml);
    }
  };

  const renderPreview = () => {
    if (!isValid) {
      return (
        <div style={{ 
          padding: '20px', 
          color: '#ef4444', 
          textAlign: 'center' 
        }}>
          Preview unavailable due to HTML errors
        </div>
      );
    }

    try {
      return (
        <div dangerouslySetInnerHTML={{ __html: html }} />
      );
    } catch (err) {
      return (
        <div style={{ 
          padding: '20px', 
          color: '#ef4444', 
          textAlign: 'center' 
        }}>
          Preview error: {err instanceof Error ? err.message : 'Unknown error'}
        </div>
      );
    }
  };

  if (!isOpen) return null;

  return (
    <EditorContainer onClick={(e) => e.target === e.currentTarget && onClose()}>
      <EditorModal>
        <EditorHeader>
          <EditorInfo>
            <EditorTitle>HTML Code Editor</EditorTitle>
            <BlockName>{blockName}</BlockName>
          </EditorInfo>
          <EditorMeta>
            <MetaItem>{blockCategory}</MetaItem>
            <CloseButton onClick={onClose}>×</CloseButton>
          </EditorMeta>
        </EditorHeader>

        {error && (
          <ErrorMessage>
            {error}
          </ErrorMessage>
        )}

        <EditorBody>
          <EditorPane>
            <PaneHeader>HTML Editor</PaneHeader>
            <EditorWrapper>
              <Editor
                height="100%"
                defaultLanguage="html"
                value={html}
                onChange={handleEditorChange}
                theme="vs-dark"
                options={{
                  minimap: { enabled: false },
                  fontSize: 14,
                  wordWrap: 'on',
                  lineNumbers: 'on',
                  scrollBeyondLastLine: false,
                  automaticLayout: true,
                  tabSize: 2,
                  insertSpaces: true,
                  formatOnPaste: true,
                  formatOnType: true,
                }}
              />
            </EditorWrapper>
          </EditorPane>

          <PreviewPane>
            <PaneHeader>Live Preview</PaneHeader>
            <PreviewContent>
              {renderPreview()}
            </PreviewContent>
          </PreviewPane>
        </EditorBody>

        <EditorActions>
          <HelpText>
            <StatusIndicator status={isValid ? 'valid' : 'invalid'} />
            {isValid ? 'HTML is valid' : 'HTML has errors'}
            <span style={{ marginLeft: '16px' }}>
              Use template variables like {'<%=it.title%>'} for dynamic content
            </span>
          </HelpText>
          
          <ActionGroup>
            <ActionButton onClick={handleReset}>
              Reset to Original
            </ActionButton>
            <ActionButton onClick={handleCancel}>
              Cancel
            </ActionButton>
            <ActionButton 
              variant="primary" 
              onClick={handleSave}
              disabled={!isValid}
            >
              Save HTML
            </ActionButton>
          </ActionGroup>
        </EditorActions>
      </EditorModal>
    </EditorContainer>
  );
};